import * as React from 'react';

import { useRouter } from 'next/router';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Tooltip, Alert, Card, Typography } from '@mui/material';

import { MyConversationsInboxType } from '@kamernet/core/ApiClient/Kamernet';
import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { AuthSource, AuthAction } from '@kamernet/core/Auth/types';
import { useAuthenticationRouting } from '@kamernet/core/Auth/useAuthenticationRouting';
import { useSessionStorage } from '@kamernet/core/BrowserStorage';
import { useIntl } from '@kamernet/core/Intl';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';

import { ListingDetailsContext } from '@kamernet/views/ListingDetails/ListingDetailsContext';

import styles from './ContactCard.module.css';

type ContactCardProps = {
  variant?: 'primary' | 'secondary';
  size?: 'large' | 'medium';
};
export const ContactCard = ({
  variant = 'primary',
  size = 'large',
}: ContactCardProps) => {
  const [, storeLastVisitedListing] = useSessionStorage('LastVisitedListing');

  const { T, locale } = useIntl();
  const router = useRouter();
  const { navigateToAuthentication } = useAuthenticationRouting();

  const { isAuthenticated, userProfile } = AuthContext.useContext();
  const { listingDetails } = ListingDetailsContext.useContext();

  const redirectToPremiumListingPage = React.useCallback(() => {
    if (listingDetails.isPreviewMode) {
      return;
    }

    storeLastVisitedListing({
      listingDetails,
      pageUrl: window.location.href,
    });

    router
      .push(ROUTES.PremiumListing.getURL({}, locale, 'prefix'))
      .catch(() => {
        // ignore
      });
  }, [listingDetails, locale, router, storeLastVisitedListing]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <React.Fragment>
      {listingDetails.isActive || listingDetails.isPreviewMode ? (
        <section>
          {!isAuthenticated && (
            <Button
              variant="contained"
              color={variant}
              size={size}
              fullWidth
              onClick={() =>
                navigateToAuthentication({
                  action: AuthAction.Signup,
                  source: AuthSource.ClickedNavbar,
                })
              }
            >
              {listingDetails.isReactForFree
                ? T('LBL_STEP4_CREDITS_REACT_FOR_FREE')
                : T('cta.contact_landlord')}
            </Button>
          )}
          {isAuthenticated &&
            (userProfile?.haspremiumaccount ||
              listingDetails.isReactForFree) && (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <React.Fragment>
                {listingDetails.isReactionMessageAlreadySent ? (
                  <React.Fragment>
                    <Card className={styles.card}>
                      <CheckCircleIcon color="primary" fontSize="large" />
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: T('toast.reply_to_ad_success'),
                        }}
                      />
                    </Card>
                    <NavLink.Native
                      href={ROUTES.Conversation.getURL(
                        {
                          inboxType: MyConversationsInboxType.Default,
                          conversationID:
                            listingDetails.reactionMessageAlreadySentId!,
                        },
                        locale,
                        'prefix',
                      )}
                      underline="none"
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={listingDetails.isPreviewMode}
                        color={variant}
                        size={size}
                      >
                        {T('cta.continue_conversation')}
                      </Button>
                    </NavLink.Native>
                  </React.Fragment>
                ) : (
                  <Tooltip
                    title={
                      listingDetails.isPreviewMode
                        ? T('ldp.lld.no_message.tooltip')
                        : ''
                    }
                  >
                    <Button
                      component={NavLink.Anchor}
                      fullWidth
                      variant="contained"
                      disabled={listingDetails.isPreviewMode}
                      color={variant}
                      size={size}
                      href={ROUTES.ReactToListing.getURL(
                        {
                          listingID: listingDetails.listingId,
                        },
                        locale,
                      )}
                    >
                      {listingDetails.isReactForFree
                        ? T('LBL_STEP4_CREDITS_REACT_FOR_FREE')
                        : T('cta.contact_landlord')}
                    </Button>
                  </Tooltip>
                )}
              </React.Fragment>
            )}
          {isAuthenticated &&
            !userProfile?.haspremiumaccount &&
            !listingDetails.isReactForFree && (
              <Tooltip
                title={
                  listingDetails.isPreviewMode
                    ? T('ldp.lld.no_message.tooltip')
                    : ''
                }
              >
                <Button
                  fullWidth
                  variant="contained"
                  disabled={listingDetails.isPreviewMode}
                  color={variant}
                  size={size}
                  onClick={redirectToPremiumListingPage}
                >
                  {listingDetails.isReactForFree
                    ? T('LBL_STEP4_CREDITS_REACT_FOR_FREE')
                    : T('cta.contact_landlord')}
                </Button>
              </Tooltip>
            )}
        </section>
      ) : (
        <Alert severity="error">{T('LBL_RA_POSSIBLE_ABUSE')}</Alert>
      )}
    </React.Fragment>
  );
};
